import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Divider, message, Modal, Tabs, Row, Col } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import InputFilterItem from '../../../../delivery/components/InputFilterItem';
import CostPeriodicTierConfig, { TierRow, DEFAULT_TIERS, PricingType } from './CostPeriodicTierConfig';
import {
  GET_PRICING_SCHEME_DETAIL,
  PricingSchemeDetailResponse,
} from 'graphql/queries/settlement/getPricingSchemeDetail';
import { NEW_PRICING_SCHEME } from 'graphql/mutations/settlement/newPricingScheme';
import { UPDATE_PRICING_SCHEME } from 'graphql/mutations/settlement/updatePricingScheme';
import { BUSINESS_SCENARIO_COST_PERIODIC, COST_PERIODIC_FEE_ITEM_CODE } from 'graphql/queries/settlement/getSettlementCommonQueries';
import { ResourceCode } from 'constants/resourceCodes';
import { HasResource } from 'components/HasResource';
import { resolvePricingSchemeMessage } from 'utils/graphqlUtils';


interface Props {
  businessScenario: number | null;
  pricingSchemeId?: number | null;
  viewMode: 'create' | 'edit' | 'view';
  onSaveSuccess?: () => void;
  onCancel?: () => void;
}

const CostPeriodicCreateSchemeComp: React.FC<Props> = ({
  businessScenario,
  pricingSchemeId,
  viewMode,
  onSaveSuccess,
  onCancel,
}) => {
  const { t } = useTranslation();
  const isView = viewMode === 'view';
  const isEdit = viewMode === 'edit';

  const [schemeName, setSchemeName] = useState('');
  const [schemeDesc, setSchemeDesc] = useState('');
  const [feeEnabled, setFeeEnabled] = useState(true);
  const [tiers, setTiers] = useState<TierRow[]>(DEFAULT_TIERS);

  const { data: detailData, loading: detailLoading } = useQuery<PricingSchemeDetailResponse>(
    GET_PRICING_SCHEME_DETAIL,
    {
      variables: { pricingSchemeId },
      skip: !pricingSchemeId,
      fetchPolicy: 'network-only',
    }
  );

  useEffect(() => {
    const detail = detailData?.getPricingSchemeDetail;
    if (detail?.messageKey || detail?.message) {
      message.error(resolvePricingSchemeMessage(detail, '加载定价方案详情失败'));
      return;
    }
    if (!detail) return;
    setSchemeName(detail.pricingSchemeName ?? '');
    setSchemeDesc(detail.pricingSchemeDescription ?? '');
    const config = detail.priceConfig?.find(p => Number(p.pricingFeeItemCode) === COST_PERIODIC_FEE_ITEM_CODE);
    if (config?.allPriceConfig?.[0]?.contentPriceConfig) {
      try {
        const parsed: any[] = JSON.parse(config.allPriceConfig[0].contentPriceConfig);
        if (parsed.length > 0) {
          // pricingType 缺失（历史数据）按 RATIO 兜底
          const rows: TierRow[] = parsed.map(t => ({
            startOrderCount: t.startOrderCount,
            endOrderCount: t.endOrderCount,
            pricingType: (t.pricingType === 'FIXED' ? 'FIXED' : 'RATIO') as PricingType,
            discountRatio: t.discountRatio ?? null,
            fixedAmount: t.fixedAmount ?? null,
          }));
          rows[rows.length - 1].endOrderCount = 99999999;
          setTiers(rows);
        }
      } catch {}
    }
  }, [detailData]);

  const [createScheme, { loading: creating }] = useMutation(NEW_PRICING_SCHEME);
  const [updateScheme, { loading: updating }] = useMutation(UPDATE_PRICING_SCHEME);

  const validate = (): string | null => {
    if (!schemeName.trim()) return '方案名称不能为空';
    if (schemeName.length > 50) return '方案名称不能超过100个字符';
    if (schemeDesc && schemeDesc.length > 50) return '方案备注不能超过100个字符';
    if (!feeEnabled) return '至少需要配置一个费用项';
    if (tiers.length === 0) return '至少需要配置一条阶梯';
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (t.startOrderCount === null || t.endOrderCount === null) {
        return `第${i + 1}行数据不完整`;
      }
      if (t.startOrderCount < 0) return `第${i + 1}行起始单量不能为负数`;
      if (t.endOrderCount <= t.startOrderCount) return `第${i + 1}行截止单量必须大于起始单量`;
      if (t.pricingType === 'FIXED') {
        if (t.fixedAmount === null) return `第${i + 1}行固定金额不能为空`;
        if (t.fixedAmount < 0) return `第${i + 1}行固定金额不能为负数`;
      } else {
        if (t.discountRatio === null) return `第${i + 1}行折扣比例不能为空`;
        if (t.discountRatio < 0 || t.discountRatio > 100) return `第${i + 1}行折扣比例须在0～100之间`;
      }
      if (i < tiers.length - 1 && tiers[i + 1].startOrderCount !== t.endOrderCount) {
        return `第${i + 1}行与第${i + 2}行单量区间不连续`;
      }
    }
    return null;
  };

  const doSave = async () => {
    const input = {
      clientId: 0,
      pricingSchemeId: isEdit ? pricingSchemeId : undefined,
      pricingSchemeName: schemeName.trim(),
      pricingSchemeDescription: schemeDesc.trim() || null,
      businessScenario: BUSINESS_SCENARIO_COST_PERIODIC,
      priceConfig: feeEnabled ? [{
        pricingFeeItemCode: COST_PERIODIC_FEE_ITEM_CODE,
        factorsType: 'nthOrderSameAddrDiscount',
        allPriceConfig: [{
          primaryFactorConfig: null,
          groupId: 0,
          contentPriceConfig: JSON.stringify(tiers.map(t => ({
            startOrderCount: t.startOrderCount,
            endOrderCount: t.endOrderCount,
            pricingType: t.pricingType,
            ...(t.pricingType === 'FIXED'
              ? { fixedAmount: t.fixedAmount }
              : { discountRatio: t.discountRatio }),
          }))),
        }],
      }] : [],
    };

    try {
      const mutation = isEdit ? updateScheme : createScheme;
      const { data, errors } = await mutation({ variables: { input } });
      if (errors?.length) { message.error(errors[0].message); return; }
      const res = isEdit
        ? data?.updatePricingSchemeByClientId
        : data?.newPricingSchemeByClientId;
      if (res?.success) {
        message.success(isEdit ? t('settlement.priceCenter.messages.updateSuccess') : t('settlement.priceCenter.messages.createSuccess'));
        onSaveSuccess?.();
      } else {
        message.error(resolvePricingSchemeMessage(res, t('settlement.priceCenter.messages.operationFailed')));
      }
    } catch (e: any) {
      message.error(e?.message || t('errors.INTERNAL_ERROR'));
    }
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { message.error(err); return; }

    if (isEdit && (detailData?.getPricingSchemeDetail?.bindCount ?? 0) > 0) {
      Modal.confirm({
        title: '确认更新',
        content: '当前方案已经绑定，编辑后将影响绑定对应的方案，是否确认更新？',
        okText: '确认',
        cancelText: '取消',
        onOk: doSave,
      });
      return;
    }
    doSave();
  };

  // 费用项 Tab 内容
  const feeTabContent = (
    <div>
      {/* 列头：费用类型固定最小宽度，阶梯配置 flex:1，折扣类型+折扣比例/固定金额固定宽度 */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, fontWeight: 500, fontSize: 13, color: '#666' }}>
        <span style={{ width: 130, flexShrink: 0 }}>费用类型</span>
        <span style={{ flex: 1 }}>阶梯配置</span>
        <div style={{ width: 380, flexShrink: 0, display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
          <span style={{ width: 200, textAlign: 'left' }}>折扣类型</span>
          <span style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>折扣比例/固定金额</span>
        </div>
      </div>
      {/* 数据行 */}
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div style={{ width: 130, flexShrink: 0, paddingTop: 5 }}>
          <Checkbox checked disabled={isView}>
            DSP基础运费
          </Checkbox>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <CostPeriodicTierConfig tiers={tiers} onChange={setTiers} readOnly={isView} discountWidth={380} />
        </div>
      </div>
    </div>
  );

  const tabItems = [
    {
      label: (
        <span>
          <Checkbox
            checked={feeEnabled}
            disabled={isView}
            onChange={e => setFeeEnabled(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          第N单N折-同地址
        </span>
      ),
      key: 'nthOrderSameAddrDiscount',
      children: feeEnabled ? feeTabContent : null,
    },
  ];

  if (detailLoading) return <div style={{ padding: 20, textAlign: 'center' }}>加载中...</div>;

  return (
    <div>
      <Row gutter={[24, 8]} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <InputFilterItem
            label="方案名称"
            spanClassName="w-[6em]"
            value={schemeName}
            onChange={e => setSchemeName(e.target.value)}
            disabled={isView}
            required
            placeholder="请输入方案名称"
          />
        </Col>
        <Col span={12}>
          <InputFilterItem
            label="方案备注"
            spanClassName="w-[6em]"
            value={schemeDesc}
            onChange={e => setSchemeDesc(e.target.value)}
            disabled={isView}
            placeholder="请输入方案备注"
          />
        </Col>
      </Row>

      <Tabs items={tabItems} size="middle" style={{ marginTop: 8 }} />

      <Divider />
      <Row justify="end" style={{ margin: 25 }}>
        <Col>
          <Button type="default" size="large" style={{ margin: 15 }} onClick={onCancel}>返回</Button>
          {!isView && (
            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_OPERATE]}>
              <Button
                type="primary"
                size="large"
                style={{ margin: 15 }}
                onClick={handleSave}
                loading={creating || updating}
              >
                保存
              </Button>
            </HasResource>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default CostPeriodicCreateSchemeComp;
